import React, { useState } from 'react';
import Page from 'src/components/common/Page';
import Breadcrumb from 'src/components/DashboardAdministrator/common/Breadcrumb';
import { EHRButton } from 'src/components/ui/v1';
import {
  Box,
  Container,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Select,
  MenuItem,
  withStyles,
} from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import Products, { IOrderProductAdmin } from 'src/services/Products';
import { ClassNameMap } from 'src/types/Dom';
import { useSuppliers } from 'src/hooks/queries/useSuppliers';
import { CSVLink, CSVLinkProps } from 'src/components/common/CSVLink';
import { UNPAGINATED_ORDER_PRODUCTS } from 'src/constants/reactQuery.keys';
import { useQueryClient } from 'react-query';
import { snakeCase } from 'lodash';
import ImportProducts from './ImportProducts';
import ProductsTable from './ProductsTable';
import CreateOrEditProduct from './CreateOrEditProduct';
import { PRODUCT_COLUMNS } from './OrderProducts.constants';

export const OrderProducts: React.FC<ClassNameMap> = ({ classes }) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [importDialogOpen, setImportDialogOpen] = useState<boolean>(false);
  const [focusProduct, setFocusProduct] = useState<IOrderProductAdmin | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [supplierId, setSupplierId] = useState<number | 'all'>('all');
  const [sort, setSort] = useState<{ sortBy: string; sortDirection: string }>({ sortBy: '', sortDirection: '' });
  const { data: suppliers = [] } = useSuppliers();

  const editProduct = (product: IOrderProductAdmin) => {
    setFocusProduct(product);
    setDialogOpen(true);
  };

  const createProduct = () => {
    setFocusProduct(undefined);
    setDialogOpen(true);
  };

  const csvCols = ['id', 'supplier_id', ...PRODUCT_COLUMNS.map((column) => snakeCase(column.field as string))];

  const handleCSV = async (): Promise<unknown[]> => {
    const data = await queryClient.fetchQuery({
      queryKey: [UNPAGINATED_ORDER_PRODUCTS, searchQuery, supplierId, sort.sortBy, sort.sortDirection],
      queryFn: () =>
        Products.getCSVProductsData({
          search: searchQuery,
          supplierId: supplierId === 'all' ? undefined : supplierId,
          [sort.sortBy]: sort.sortDirection,
        }),
    });

    return data?.data || [];
  };

  const csvProps: CSVLinkProps = {
    getCSVRows: () => handleCSV(),
    csvName: 'Order Products',
    csvCols,
    customButton: (
      <EHRButton text="Export data" color="primary" dataCy="exportDataButton" className={classes?.button} />
    ),
  };

  return (
    <Page title="Products">
      <Container classes={classes}>
        <Breadcrumb prependHeading="Inventory" />
        <Box display="flex" justifyContent="space-between" alignItems="center" margin={0} paddingY="8px">
          <Box display="flex" alignItems="center" flex={1} marginRight={2}>
            <FormControl variant="outlined" className={classes?.searchField}>
              <OutlinedInput
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputProps={{
                  'data-cy': 'products-search-input',
                  'aria-label': 'Search products',
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
              />
            </FormControl>
            <FormControl variant="outlined" className={classes?.filterField}>
              <Select
                inputProps={{
                  'data-cy': 'products-supplier-select',
                  'aria-label': 'Filter by supplier',
                }}
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value as number | 'all')}
                displayEmpty
              >
                <MenuItem value="all">All Suppliers</MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box display="flex">
            <CSVLink {...csvProps} />
            <EHRButton
              text="Import Products"
              color="primary"
              dataCy="importProductsButton"
              className={classes?.button}
              onClick={() => setImportDialogOpen(true)}
            />
            <EHRButton
              text="New Product"
              color="primary"
              dataCy="newProductButton"
              className={classes?.button}
              onClick={createProduct}
            />
          </Box>
        </Box>
        <ProductsTable
          editProduct={editProduct}
          searchQuery={searchQuery}
          supplierId={supplierId}
          sortQuery={(sortBy: string, sortDirection: string) => setSort({ sortBy, sortDirection })}
        />
        <CreateOrEditProduct open={dialogOpen} dismiss={() => setDialogOpen(false)} row={focusProduct} />
        <ImportProducts open={importDialogOpen} dismiss={() => setImportDialogOpen(false)} upsert />
      </Container>
    </Page>
  );
};

export default withStyles({
  maxWidthLg: {
    maxWidth: 'unset',
  },
  button: {
    margin: 'auto',
    marginRight: '0',
    width: 135,
    marginLeft: 10,
  },
  searchField: {
    paddingLeft: 20,
    width: 300,
    marginRight: 16,
  },
  filterField: {
    width: 200,
  },
})(OrderProducts);
